import { ReportStatus } from '@signalvote/shared';
import { assertNever } from '@signalvote/shared';
import type { ComplianceReportDetailDto, ComplianceReportDto, FilingChainEntryDto } from '@signalvote/shared';
import { FilingLockCopy } from './filingLockCopy';

/**
 * Has this report been filed at least once? Mirrors the backend's
 * `ReportStatusExtensions.IsPostFiling` — `Filed` plus the two amendment states, past which a
 * report's figures are a replay of the file it sent rather than a live recompute.
 *
 * Distinct from `status === Ready`, which is the narrower "still editable" gate: a `NotStarted`
 * report is neither editable nor filed, so the two are not each other's negation.
 */
export function isPostFiling(status: ReportStatus): boolean {
  return (
    status === ReportStatus.Filed ||
    status === ReportStatus.NeedsAmendment ||
    status === ReportStatus.Amended
  );
}

/**
 * The chain's amendment not yet filed, if any. The server allows one at a time (it refuses a second
 * with `AmendmentInProgress`), so an amend offer on any report in the chain continues this one.
 */
export function openAmendment(chain: FilingChainEntryDto[] | undefined): FilingChainEntryDto | null {
  return chain?.find((e) => e.amendmentNumber > 0 && !isPostFiling(e.status)) ?? null;
}

/**
 * Where a report stands in its filing chain. A superseded (Amended) report is not the filing in
 * force: corrections go on the chain's current head. A filed report whose amendment is open shares
 * that amendment's period, so the work happens on the amendment. Anything else is in force.
 */
export type ChainStanding =
  | { kind: 'inForce' }
  | { kind: 'superseded'; head: FilingChainEntryDto | null }
  | { kind: 'amendmentInProgress'; amendment: FilingChainEntryDto };

export function chainStanding(
  report: Pick<ComplianceReportDto, 'id' | 'status'>,
  chain: FilingChainEntryDto[] | undefined,
): ChainStanding {
  if (report.status === ReportStatus.Amended) {
    return { kind: 'superseded', head: chain?.find((e) => e.isCurrentHead) ?? null };
  }
  const open = openAmendment(chain);
  if (open && open.complianceReportId !== report.id) return { kind: 'amendmentInProgress', amendment: open };
  return { kind: 'inForce' };
}

/**
 * Does the report's detail page show a reopen control? It has one only through
 * ReopenReportAffordance (`canReopen`) or ReopenPeriodAffordance (locked or reopened period).
 * A "reopen" link elsewhere must check this, or it lands on a page with nothing to click.
 */
export function hasReopenControl(
  report: Pick<ComplianceReportDto, 'canReopen' | 'locked' | 'manuallyUnlocked'>,
): boolean {
  return report.canReopen || report.locked || report.manuallyUnlocked;
}

/**
 * Count of in-period transactions that are NOT on the filing because they were
 * still Pending when the report was filed (`inclusion === 'ExcludedPending'`).
 *
 * This is the single source of truth for "is this period actually done?" — it
 * reads the SAME `inclusion` flag the "Pending at filing" list uses, so the
 * post-file success panel and the pending list can never disagree. Both the
 * report-detail success/warning gate and the `PostFileSuccessPanel`'s own mirror
 * guard call it, and the mark-filed confirm dialog gates on it.
 *
 * Voided / status-drift exclusions are deliberately NOT counted: only Pending
 * rows are the actionable "not reported yet, but could be" class. Under
 * Alabama's two-axis model a report can be Filed in the portal while these rows
 * still need exporting + uploading to the FCPA portal (ADR-020), so their
 * presence is a warning, never a block.
 */
export function pendingAtFilingCount(
  report: Pick<ComplianceReportDetailDto, 'contributionItems' | 'expenditureItems'>,
): number {
  const isPending = (item: { inclusion: string }) => item.inclusion === 'ExcludedPending';
  return (
    report.contributionItems.filter(isPending).length +
    report.expenditureItems.filter(isPending).length
  );
}

/**
 * ADR-019 — the computed-period-lock chip. Reads the DTO's `locked` /
 * `manuallyUnlocked` fields (never re-derives the calendar predicate). A
 * manually-reopened period reads "Reopened"; an otherwise-locked period reads
 * "Locked"; an open period has no chip (null). The override wins, so when
 * `manuallyUnlocked` is true the server already reports `locked` as false.
 * A superseded (Amended) report has no chip: the period's lock belongs to the filing in force.
 */
export function lockBadge(
  report: Pick<ComplianceReportDto, 'status' | 'locked' | 'manuallyUnlocked'>,
): { label: string; tooltip: string; className: string } | null {
  const { locked, manuallyUnlocked } = report;
  if (report.status === ReportStatus.Amended) return null;
  if (manuallyUnlocked) {
    return {
      label: FilingLockCopy.ReopenedBadge,
      tooltip: FilingLockCopy.ReopenedBanner,
      className: 'bg-amber-100 text-amber-800',
    };
  }
  if (locked) {
    return {
      label: FilingLockCopy.LockedBadge,
      tooltip: FilingLockCopy.LockedBadgeTooltip,
      className: 'bg-gray-200 text-gray-700',
    };
  }
  return null;
}

// An amendment stays Ready until it is filed; to the treasurer that is "Amending".
export function statusBadge(
  report: Pick<ComplianceReportDto, 'status' | 'amendmentNumber'>,
): { label: string; className: string } {
  const { status } = report;
  if (status === ReportStatus.Ready && report.amendmentNumber > 0) {
    return { label: 'Amending', className: 'bg-orange-100 text-orange-700' };
  }
  switch (status) {
    case ReportStatus.NotStarted:
      return { label: 'Not Started', className: 'bg-gray-100 text-gray-700' };
    case ReportStatus.Ready:
      return { label: 'Ready', className: 'bg-blue-100 text-blue-700' };
    case ReportStatus.Filed:
      return { label: 'Filed', className: 'bg-green-100 text-green-700' };
    case ReportStatus.NeedsAmendment:
      return { label: 'Needs Amendment', className: 'bg-orange-100 text-orange-700 animate-pulse' };
    case ReportStatus.Amended:
      return { label: 'Amended', className: 'bg-gray-100 text-gray-500 line-through' };
    default:
      return assertNever(status);
  }
}
